// Tiny fetch wrapper used by every TanStack Query hook in this folder.
// Resolves the API base URL from the runtime config injected via
// /env.js (`window.__APP_CONFIG__.apiBaseUrl`) so the same JS bundle
// works in `vite dev` (proxied to localhost:8000), in the packaged
// nginx image (rewritten by the entrypoint), and in tests (default
// `/api`).
//
// Auth model (see ../backend/AGENTS.md — Authentication roadmap):
// - Basic auth: the AuthGate stashes `Basic base64(user:pass)` in
//   sessionStorage; we attach it as the `Authorization` header.
// - OIDC: the backend drives the OAuth flow and writes a signed
//   HTTP-only session cookie. We send `credentials: "include"` on
//   every request so the cookie is forwarded (and Set-Cookie from
//   the callback redirect is accepted) — the SPA never sees the
//   token itself.

import type { ApiErrorBody } from "./types";

/** Default base when no runtime config is present (test / dev safe net). */
const DEFAULT_BASE = "/api";
const AUTH_STORAGE_KEY = "helmdiver:auth:basic";

export class ApiError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody | null;

  constructor(status: number, message: string, body: ApiErrorBody | null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

export function getBasicAuthToken(): string | null {
  if (typeof window === "undefined") return null;
  const raw = window.sessionStorage.getItem(AUTH_STORAGE_KEY);
  return raw && raw.trim() ? raw : null;
}

export function setBasicAuthCredentials(username: string, password: string): void {
  if (typeof window === "undefined") return;
  const encoded = btoa(`${username}:${password}`);
  window.sessionStorage.setItem(AUTH_STORAGE_KEY, `Basic ${encoded}`);
}

export function clearBasicAuthCredentials(): void {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(AUTH_STORAGE_KEY);
}

/** Read the apiBaseUrl from window.__APP_CONFIG__ at *call* time so tests
 *  can mutate the global between cases without re-importing the module. */
export function apiBaseUrl(): string {
  if (typeof window === "undefined") return DEFAULT_BASE;
  const configured = window.__APP_CONFIG__?.apiBaseUrl;
  if (typeof configured === "string" && configured.trim() !== "") {
    return configured.replace(/\/+$/, "");
  }
  return DEFAULT_BASE;
}

/** Build a full URL from a relative API path and an optional query map. */
export function apiUrl(
  path: string,
  query?: Record<string, string | number | boolean | undefined>,
): string {
  const base = apiBaseUrl();
  const normalised = path.startsWith("/") ? path : `/${path}`;
  const url = `${base}${normalised}`;
  if (!query) return url;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) continue;
    search.append(key, String(value));
  }
  const qs = search.toString();
  return qs ? `${url}?${qs}` : url;
}

interface RequestOptions {
  signal?: AbortSignal;
  query?: Record<string, string | number | boolean | undefined>;
}

/** GET <apiBaseUrl><path> and JSON-decode the body, throwing ApiError
 *  on any non-2xx response. The decoded shape is unverified — callers
 *  cast to the type they expect. */
export async function apiGet<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const basicAuth = getBasicAuthToken();
  const response = await fetch(apiUrl(path, options.query), {
    method: "GET",
    headers: {
      Accept: "application/json",
      ...(basicAuth ? { Authorization: basicAuth } : {}),
    },
    signal: options.signal,
    // The OIDC BFF flow relies on a signed session cookie set by the
    // backend on /api/auth/callback. `include` ensures the cookie is
    // sent on every API call (both same-origin in the packaged image
    // and cross-origin when running `vite dev` against localhost:8000).
    credentials: "include",
  });

  if (!response.ok) {
    const body = await safeJson<ApiErrorBody>(response);
    const detail = extractDetail(body);
    throw new ApiError(
      response.status,
      detail ?? `${response.status} ${response.statusText}`,
      body,
    );
  }

  return (await response.json()) as T;
}

/** POST <apiBaseUrl><path>; used for logout (no body, no JSON parse). */
export async function apiPost(
  path: string,
  options: RequestOptions = {},
): Promise<void> {
  const basicAuth = getBasicAuthToken();
  const response = await fetch(apiUrl(path, options.query), {
    method: "POST",
    headers: {
      Accept: "application/json",
      ...(basicAuth ? { Authorization: basicAuth } : {}),
    },
    signal: options.signal,
    credentials: "include",
  });
  if (!response.ok) {
    const body = await safeJson<ApiErrorBody>(response);
    const detail = extractDetail(body);
    throw new ApiError(
      response.status,
      detail ?? `${response.status} ${response.statusText}`,
      body,
    );
  }
}

interface JsonPostOptions extends RequestOptions {
  body: unknown;
}

/** Authenticated JSON POST that returns a decoded response body. */
export async function apiPostJson<TResponse>(
  path: string,
  options: JsonPostOptions,
): Promise<TResponse> {
  const basicAuth = getBasicAuthToken();
  const response = await fetch(apiUrl(path, options.query), {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(basicAuth ? { Authorization: basicAuth } : {}),
    },
    body: JSON.stringify(options.body),
    signal: options.signal,
    credentials: "include",
  });
  if (!response.ok) {
    const body = await safeJson<ApiErrorBody>(response);
    const detail = extractDetail(body);
    throw new ApiError(
      response.status,
      detail ?? `${response.status} ${response.statusText}`,
      body,
    );
  }
  return (await response.json()) as TResponse;
}

async function safeJson<T>(response: Response): Promise<T | null> {
  try {
    return (await response.json()) as T;
  } catch {
    return null;
  }
}

function extractDetail(body: ApiErrorBody | null): string | undefined {
  if (!body) return undefined;
  if (typeof body.detail === "string") return body.detail;
  if (Array.isArray(body.detail)) {
    return body.detail
      .map((entry) => entry.msg)
      .filter(Boolean)
      .join("; ");
  }
  return undefined;
}
